import './styles/tailwind.css';
import React, { useEffect } from 'react';
import { listenForInstallPrompt } from './lib/addToHomeScreen';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Outlet,
  useParams,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuth } from './scripts/AuthContext';
import { OrgFilterProvider } from './scripts/OrgFilterContext';
import { OrgBrandThemeProvider } from './scripts/OrgBrandThemeProvider';
import { SocialPlaneThemeProvider } from './scripts/SocialPlaneThemeProvider';
import { DmSocketProvider } from './scripts/DmSocketContext';
import { SocialDmSocketProvider } from './scripts/SocialDmSocketContext';
import { PartnerSocketProvider } from './scripts/PartnerSocketContext';
import { NotificationsSocketProvider } from './scripts/NotificationsSocketContext';
import { DmDrawerProvider } from './scripts/DmDrawerContext';
import { DmAlertProvider } from './scripts/DmAlertContext';
import { SavedPostsProvider } from './scripts/SavedPostsContext';
import { SocialSavedPostsProvider } from './scripts/SocialSavedPostsContext';
import AutoAwayPresenceHost from './hooks/useAutoAwayPresence';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { ConfirmDialogProvider } from './components/ConfirmDialogProvider';
import AppSidebar from './components/AppSidebar';
import NotificationBell from './components/NotificationBell';
import SearchBar from './components/SearchBar';
import SocialBottomNav, {
  isSocialMobileThreadPath,
} from './components/SocialBottomNav';
import WorkplaceBottomNav, {
  isWorkplaceMobileThreadPath,
} from './components/WorkplaceBottomNav';
import DmDrawerHost from './components/DmDrawerHost';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import Signup from './components/Signup';
import VerifyEmail from './components/VerifyEmail';
import AboutPage from './components/AboutPage';
import InviteAcceptPage from './components/InviteAcceptPage';
import OrgDetail from './components/OrgDetail';
import OrgSettingsPage from './components/OrgSettingsPage';
import OrgSsoEnterPage from './components/OrgSsoEnterPage';
import CommunitySpacePage from './components/CommunitySpacePage';
import OrgSocialHomePage from './components/orgSocial/OrgSocialHomePage';
import OrgSocialExplorePage from './components/orgSocial/OrgSocialExplorePage';
import OrgSocialMessagesPage from './components/orgSocial/OrgSocialMessagesPage';
import OrgSocialProfileEditPage from './components/orgSocial/OrgSocialProfileEditPage';
import SocialSetupPage from './components/SocialSetupPage';
import SocialExplorePage from './components/SocialExplorePage';
import SocialSearchPage from './components/SocialSearchPage';
import CreateCommunityPage from './components/CreateCommunityPage';
import SocialCommunityPage from './components/SocialCommunityPage';
import SocialProfilePage from './components/SocialProfilePage';
import SocialOrgProfilePage from './components/SocialOrgProfilePage';
import SocialHardGate from './components/SocialHardGate';
import TeamDetail from './components/TeamDetail';
import TeamSettingsPage from './components/TeamSettingsPage';
import Profile from './components/Profile';
import PostPermalinkPage from './components/PostPermalinkPage';
import SocialPostPermalinkPage from './components/SocialPostPermalinkPage';
import OrgSearchPage from './components/OrgSearchPage';
import MemberProfilePage from './components/MemberProfilePage';
import MessagesPage from './components/MessagesPage';
import SocialMessagesPage from './components/SocialMessagesPage';
import MyWorkPage from './components/MyWorkPage';
import AnalyticsRouteListener from './components/AnalyticsRouteListener';
import AnalyticsConsentBanner from './components/AnalyticsConsentBanner';

import WorkspaceTour from './components/WorkspaceTour';
import { SocialProfileProvider, useSocialProfile } from './scripts/SocialProfileContext';
import { useOrgFilter } from './scripts/OrgFilterContext';
import { safeInviteReturn, safePostLoginReturn } from './lib/inviteReturn';
import AppsInstallPage from './components/AppsInstallPage';

function RedirectOrgCommunityToSocial() {
  const { orgId } = useParams();
  return (
    <Navigate
      to={`/orgs/${encodeURIComponent(orgId)}/social`}
      replace
    />
  );
}

/** Logged-in /login and /signup honor a safe invite magic-link return. */
function AuthHomeRedirect() {
  const [params] = useSearchParams();
  const dest =
    safePostLoginReturn(params.get('return')) ||
    safeInviteReturn(params.get('return')) ||
    '/dashboard';
  return <Navigate to={dest} replace />;
}

/** True for /orgs/:orgId/social and nested act-as Circa Social routes. */
function isOrgSocialImmersionPath(pathname) {
  return /^\/orgs\/[^/]+\/social(?:\/|$)/.test(String(pathname || ''));
}

/** App chrome with sidebar; /about is registered outside this layout. */
const AppShellLayout = () => {
  const { selectedOrgId } = useOrgFilter();
  const { pathname } = useLocation();
  const { complete: socialComplete } = useSocialProfile();
  const isOrgSocialImmersion = isOrgSocialImmersionPath(pathname);
  const isPersonalSocial = selectedOrgId === 'social';
  const hasOrgSelected =
    Boolean(selectedOrgId) && selectedOrgId !== 'social';
  const isSocialWorkspace =
    !selectedOrgId || selectedOrgId === 'social' || isOrgSocialImmersion;
  const showSocialBottomNav =
    isPersonalSocial &&
    socialComplete &&
    !pathname.startsWith('/social/setup') &&
    !isSocialMobileThreadPath(pathname);
  const showWorkplaceBottomNav =
    hasOrgSelected &&
    !isOrgSocialImmersion &&
    !isWorkplaceMobileThreadPath(pathname);
  const showMobileBottomNav = showSocialBottomNav || showWorkplaceBottomNav;
  const slimAppHeader = isPersonalSocial || hasOrgSelected;
  const hideAppHeaderOnMobileThread =
    isSocialMobileThreadPath(pathname) ||
    isWorkplaceMobileThreadPath(pathname);

  if (isOrgSocialImmersion) {
    return (
      <SidebarProvider>
        <SidebarInset
          data-social-plane
          className="min-w-0 h-svh max-h-svh overflow-hidden circa-orbit-bg-subtle"
        >
          <SocialHardGate>
            <Outlet />
          </SocialHardGate>
          <DmDrawerHost />
        </SidebarInset>
        <Toaster
          theme="light"
          richColors
          closeButton
          position="top-right"
        />
      </SidebarProvider>
    );
  }

  return (
  <SidebarProvider>
    <AppSidebar />
    <SidebarInset className="h-svh max-h-svh min-w-0 overflow-hidden circa-orbit-bg-subtle">
      <header
        className={`sticky top-0 z-20 flex shrink-0 items-center border-b border-border bg-background/90 backdrop-blur-sm px-4 ${slimAppHeader ? "h-12 gap-3 md:h-14 md:gap-4" : "h-14 gap-4"}${hideAppHeaderOnMobileThread ? " max-md:hidden" : ""}`}
      >
        <SidebarTrigger className={slimAppHeader ? "h-10 w-10 md:h-7 md:w-7" : undefined} />
        {isPersonalSocial ? (
          <p className="min-w-0 flex-1 truncate text-sm font-semibold">
            Circa Social
          </p>
        ) : (
          <div className="flex min-w-0 flex-1 justify-center">
            <SearchBar />
          </div>
        )}
        <div data-tour="notifications">
          <NotificationBell />
        </div>
      </header>
      <div
        className={`flex min-h-0 flex-1 flex-col overflow-y-auto${
          showMobileBottomNav
            ? " max-md:pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))]"
            : ""
        }`}
      >
        <SocialHardGate>
          <Outlet />
        </SocialHardGate>
      </div>
      {showSocialBottomNav ? <SocialBottomNav /> : null}
      {showWorkplaceBottomNav ? (
        <WorkplaceBottomNav orgId={selectedOrgId} />
      ) : null}
      <DmDrawerHost />
    </SidebarInset>
    <WorkspaceTour />
    <Toaster
      theme={isSocialWorkspace ? 'light' : 'dark'}
      richColors
      closeButton
      position="top-right"
    />
  </SidebarProvider>
  );
};

const App = () => {
  const { isAuthenticated, loading } = useAuth();

  useEffect(() => {
    listenForInstallPrompt();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AnalyticsRouteListener />
      <AnalyticsConsentBanner />
      {isAuthenticated ? (
        <OrgFilterProvider>
          <OrgBrandThemeProvider>
            <SocialPlaneThemeProvider>
            <NotificationsSocketProvider>
            <DmSocketProvider>
              <SocialDmSocketProvider>
              <PartnerSocketProvider>
              <DmDrawerProvider>
                <DmAlertProvider>
                  <AutoAwayPresenceHost />
                  <SavedPostsProvider>
                    <SocialSavedPostsProvider>
                    <ConfirmDialogProvider>
                      <SocialProfileProvider>
                      <Routes>
                        <Route path="/about" element={<AboutPage />} />
                        <Route
                          path="/invite/:token"
                          element={<InviteAcceptPage />}
                        />
                        <Route path="/login" element={<AuthHomeRedirect />} />
                        <Route path="/signup" element={<AuthHomeRedirect />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route element={<AppShellLayout />}>
                          <Route path="/" element={<Navigate to="/dashboard" />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route
                            path="/apps/install"
                            element={<AppsInstallPage />}
                          />
                          <Route path="/social/setup" element={<SocialSetupPage />} />
                          <Route path="/explore" element={<SocialExplorePage />} />
                          <Route path="/search" element={<SocialSearchPage />} />
                          <Route
                            path="/communities/new"
                            element={<CreateCommunityPage />}
                          />
                          <Route
                            path="/c/:communityId"
                            element={<SocialCommunityPage />}
                          />
                          <Route
                            path="/u/:handle"
                            element={<SocialProfilePage />}
                          />
                          <Route
                            path="/org/:handle"
                            element={<SocialOrgProfilePage />}
                          />
                          <Route
                            path="/my-orgs"
                            element={<Navigate to="/dashboard" replace />}
                          />
                          <Route path="/orgs/:orgId" element={<OrgDetail />} />
                          <Route
                            path="/orgs/:orgId/sso"
                            element={<OrgSsoEnterPage />}
                          />
                          <Route
                            path="/orgs/:orgId/settings"
                            element={<OrgSettingsPage />}
                          />
                          <Route
                            path="/orgs/:orgId/community"
                            element={<RedirectOrgCommunityToSocial />}
                          />
                          <Route
                            path="/orgs/:orgId/community/*"
                            element={<RedirectOrgCommunityToSocial />}
                          />
                          <Route
                            path="/orgs/:orgId/social"
                            element={<CommunitySpacePage />}
                          >
                            <Route index element={<OrgSocialHomePage />} />
                            <Route
                              path="explore"
                              element={<OrgSocialExplorePage />}
                            />
                            <Route path="search" element={<SocialSearchPage />} />
                            <Route
                              path="messages"
                              element={<OrgSocialMessagesPage />}
                            />
                            <Route
                              path="messages/:conversationId"
                              element={<OrgSocialMessagesPage />}
                            />
                            <Route
                              path="profile"
                              element={<OrgSocialProfileEditPage />}
                            />
                            <Route
                              path="u/:handle"
                              element={<SocialProfilePage />}
                            />
                            <Route
                              path="org/:handle"
                              element={<SocialOrgProfilePage />}
                            />
                            <Route
                              path="c/:communityId"
                              element={<SocialCommunityPage />}
                            />
                            <Route
                              path="p/:postId"
                              element={<SocialPostPermalinkPage />}
                            />
                          </Route>
                          <Route path="/teams/:teamId" element={<TeamDetail />} />
                          <Route
                            path="/teams/:teamId/settings"
                            element={<TeamSettingsPage />}
                          />
                          <Route
                            path="/o/:orgId/p/:postId"
                            element={<PostPermalinkPage />}
                          />
                          <Route
                            path="/p/:postId"
                            element={<SocialPostPermalinkPage />}
                          />
                          <Route path="/o/:orgId/search" element={<OrgSearchPage />} />
                          <Route path="/o/:orgId/my-work" element={<MyWorkPage />} />
                          <Route
                            path="/o/:orgId/members/:userId"
                            element={<MemberProfilePage />}
                          />
                          <Route path="/messages" element={<SocialMessagesPage />} />
                          <Route
                            path="/messages/:conversationId"
                            element={<SocialMessagesPage />}
                          />
                          <Route
                            path="/o/:orgId/messages"
                            element={<MessagesPage />}
                          />
                          <Route
                            path="/o/:orgId/messages/partner/:partnerChannelId"
                            element={<MessagesPage />}
                          />
                          <Route
                            path="/o/:orgId/messages/:conversationId"
                            element={<MessagesPage />}
                          />
                          <Route path="/profile" element={<Profile />} />
                        </Route>
                      </Routes>
                      </SocialProfileProvider>
                    </ConfirmDialogProvider>
                    </SocialSavedPostsProvider>
                  </SavedPostsProvider>
                </DmAlertProvider>
              </DmDrawerProvider>
              </PartnerSocketProvider>
              </SocialDmSocketProvider>
            </DmSocketProvider>
            </NotificationsSocketProvider>
            </SocialPlaneThemeProvider>
          </OrgBrandThemeProvider>
        </OrgFilterProvider>
      ) : (
        <Routes>
          <Route path="/" element={<AboutPage />} />
          <Route path="/about" element={<Navigate to="/" replace />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route
            path="/apps/install"
            element={<AppsInstallLoginRedirect />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </Router>
  );
};

function AppsInstallLoginRedirect() {
  const [params] = useSearchParams();
  const pack = String(params.get('pack') || '').trim();
  const dest = pack
    ? `/apps/install?pack=${encodeURIComponent(pack)}`
    : '/apps/install';
  return (
    <Navigate
      to={`/login?return=${encodeURIComponent(dest)}`}
      replace
    />
  );
}

export default App;
